import React, { useEffect, useRef, useState } from "react";
import { FormDropdownOption } from "../FormTypes";

interface PrioritizedListPickerProps {
  id: string;
  placeholder?: string;
  required?: boolean;
  value?: string[];
  options?: FormDropdownOption[];
  onChange: (value: string[]) => void;
  instructionText?: string;
  listTagText?: string;
}

const PrioritizedListPicker: React.FC<PrioritizedListPickerProps> = ({
  id,
  placeholder,
  required,
  value = [],
  options = [],
  onChange,
  instructionText,
  listTagText,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const selectedTexts: string[] = Array.isArray(value) ? value : [];

  const renderSummaryLabel = () => {
    if (selectedTexts.length === 0) {
      return placeholder || "Select options";
    }

    return (
      <>
        {selectedTexts.map((text, index) => (
          <span key={`${text}-${index}`}>
            {index === 0 ? (
              <span style={{ color: "green", fontWeight: "600" }}>{text}</span>
            ) : (
              text
            )}
            {index < selectedTexts.length - 1 && ", "}
          </span>
        ))}
      </>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const toggleKey = (opt: FormDropdownOption) => {
    const current = new Set<string>(selectedTexts);
    const text = String(opt.text ?? "");
    if (current.has(text)) {
      current.delete(text);
    } else {
      current.add(text);
    }
    onChange(Array.from(current));
  };

  return (
    <div className="prioritized-list-picker">
      <div className="list-dropdown-wrapper" ref={containerRef}>
        <button
          type="button"
          id={id}
          className="form-select list-dropdown-multi-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="list-dropdown-multi-label">
            {renderSummaryLabel()}
          </span>
          <span className="list-dropdown-multi-caret"></span>
        </button>

        {open && (
          <div
            className="list-dropdown-multi-menu"
            role="listbox"
            aria-multiselectable="true"
          >
            {instructionText && (
              <div className="list-dropdown-instruction-header">
                {instructionText}
              </div>
            )}
            {options.map((o) => {
              const text = String(o.text ?? "");
              const isSelected = selectedTexts.includes(text);
              const isPrimary =
                selectedTexts.length > 0 && selectedTexts[0] === text;

              return (
                <button
                  key={o.key}
                  type="button"
                  className={
                    "list-dropdown-multi-option" +
                    (isSelected ? " list-dropdown-multi-option--selected" : "")
                  }
                  onClick={() => toggleKey(o)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="list-dropdown-multi-option-check">
                    {isSelected ? "✓" : ""}
                  </span>
                  <span className="list-dropdown-multi-option-text">
                    {o.text}
                  </span>
                  {listTagText && isPrimary && (
                    <span className="prioritized-primary-tag">
                      {listTagText}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrioritizedListPicker;
